import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { fabric } from 'fabric';
import ArrowIcon from '../images/Icon20_Arrow.svg';
import {
  setCursorTooltip,
  updateTool,
} from '../../../redux/actions/canvas_editor';
import Button from './Toolbar/Button';
import { connect } from 'react-redux';
import { __ } from '../../../core/utils';
import {
  createArrowHeadForLine,
  getThicknessPx,
  getDrawingBounds,
  linkArrowObjects,
  syncArrowHeadWithLine,
} from '../helpers/fabricHelpers';

const MIN_ARROW_LENGTH = 2;

const clampPointToCanvas = (point, width, height) => ({
  x: Math.max(0, Math.min(width, point.x)),
  y: Math.max(0, Math.min(height, point.y)),
});

const updateArrowPreview = (line, head, start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);

  line.set({
    x1: start.x,
    y1: start.y,
    x2: end.x,
    y2: end.y,
  });
  line.setCoords();

  if (length === 0) {
    head.set({ left: start.x, top: start.y });
    head.setCoords();
    return;
  }

  syncArrowHeadWithLine(line);
};

const Arrow = ({
  canvas_editor: { viewer, color, selectedThickness, fabricCanvases },
  isActive,
  source: { width, height },
  sourceType,
  user_id,
  user_full_name,
}) => {
  const [state, setState] = useState({
    line: null,
    head: null,
    isMouseDown: false,
    startPoint: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!isActive || !fabricCanvases) return;

    setCursorTooltip(__('canvas.tooltip.line.start'));

    const disposers = [];

    Object.entries(fabricCanvases).forEach(([pageNumber, canvas]) => {
      canvas.defaultCursor = 'crosshair';
      canvas.hoverCursor = 'crosshair';
      canvas.selection = false;

      canvas.getObjects().forEach((obj) => {
        obj.selectable = false;
        obj.evented = false;
      });

      const onPointerDown = () => {
        viewer?.setMouseNavEnabled(false);
      };

      const onMouseDown = (options) => {
        if (options.target || !isActive) return;

        const rawPointer = canvas.getPointer(options.e);
        const bounds = getDrawingBounds(canvas, { width, height }, sourceType);
        const pointer = clampPointToCanvas(
          rawPointer,
          bounds.width,
          bounds.height
        );
        const pdfScaleFactor =
          sourceType === 'pdf' ? canvas?.pdfScaleFactor || 1 : 1;
        const strokeWidth =
          getThicknessPx(selectedThickness.key) * pdfScaleFactor;

        const line = new fabric.Line(
          [pointer.x, pointer.y, pointer.x, pointer.y],
          {
            stroke: color.hex,
            strokeWidth,
            selectable: false,
            evented: false,
            hasBorders: false,
            hasControls: false,
            objectType: 'arrow',
            pageNumber,
            objectCaching: false,
            strokeUniform: true,
            userID: user_id,
            userFullName: user_full_name,
            isTemporary: true,
          }
        );

        const head = createArrowHeadForLine(line);

        canvas.add(line);
        if (head) {
          canvas.add(head);
          linkArrowObjects(line, head);
        }

        setState({
          line,
          head,
          isMouseDown: true,
          startPoint: pointer,
        });
      };

      const onMouseMove = (options) => {
        if (
          !stateRef.current.isMouseDown ||
          !stateRef.current.line ||
          !stateRef.current.head ||
          !stateRef.current.startPoint
        ) {
          return;
        }

        const rawPointer = canvas.getPointer(options.e);
        const bounds = getDrawingBounds(canvas, { width, height }, sourceType);
        const pointer = clampPointToCanvas(
          rawPointer,
          bounds.width,
          bounds.height
        );

        updateArrowPreview(
          stateRef.current.line,
          stateRef.current.head,
          stateRef.current.startPoint,
          pointer
        );

        canvas.requestRenderAll();
        setCursorTooltip(__('canvas.tooltip.line.drawing'));
      };

      const onMouseUp = (options) => {
        const { isMouseDown, line, head, startPoint } = stateRef.current;
        if (!isMouseDown || !line || !head || !startPoint) return;

        const rawPointer = options?.e
          ? canvas.getPointer(options.e)
          : { x: line.x2, y: line.y2 };
        const bounds = getDrawingBounds(canvas, { width, height }, sourceType);
        const endPoint = clampPointToCanvas(
          rawPointer,
          bounds.width,
          bounds.height
        );
        const dx = endPoint.x - startPoint.x;
        const dy = endPoint.y - startPoint.y;
        const length = Math.sqrt(dx * dx + dy * dy);

        if (length < MIN_ARROW_LENGTH) {
          if (head) canvas.remove(head);
          canvas.remove(line);
        } else {
          updateArrowPreview(line, head, startPoint, endPoint);

          line.set({
            selectable: true,
            evented: true,
            hasBorders: false,
            hasControls: false,
            objectCaching: false,
            lockRotation: true,
            startLeft: line.left,
            startTop: line.top,
            isTemporary: false,
          });

          head?.set({
            isTemporary: false,
          });

          line.setCoords();
          head?.setCoords?.();
          canvas.requestRenderAll();
          canvas.fire('object:added', { target: line });
        }

        setState({
          line: null,
          head: null,
          isMouseDown: false,
          startPoint: null,
        });

        updateTool({ tool: 'select' });
      };

      canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);
      canvas.on('mouse:down', onMouseDown);
      canvas.on('mouse:move', onMouseMove);
      canvas.on('mouse:up', onMouseUp);

      disposers.push(() => {
        if (canvas?.upperCanvasEl) {
          canvas.upperCanvasEl.removeEventListener(
            'pointerdown',
            onPointerDown
          );
        }

        canvas.off('mouse:down', onMouseDown);
        canvas.off('mouse:move', onMouseMove);
        canvas.off('mouse:up', onMouseUp);
      });
    });

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }, [
    isActive,
    color,
    selectedThickness,
    fabricCanvases,
    width,
    height,
    sourceType,
  ]);

  const toggleTool = () => {
    updateTool({ tool: 'arrow' });
  };

  return (
    <Button
      id='arrow'
      label='Arrow'
      icon={ArrowIcon}
      active={isActive}
      onClick={toggleTool}
    />
  );
};

Arrow.propTypes = {
  isActive: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  canvas_editor: state.canvas_editor,
  user_id: state.auth.id,
  user_full_name: state.auth.firstname + ' ' + state.auth.lastname,
});

export default connect(mapStateToProps, null)(Arrow);
